import Checkbox from '@/components/atomic/checkbox'
import Icon, { IconName } from '@/components/atomic/icon'
import Text, { FontWeight, TextVariant } from '@/components/atomic/text'
import Tooltip from '@/components/atomic/tooltip'
import { cn } from '@/utils/shadcn/utils'

interface ModelConstraintCardContainerProps {
  children: React.ReactNode
  className?: string
}

interface ModelConstraintContentProps {
  children: React.ReactNode
}

interface ModelConstraintCardHeaderProps {
  title: string
  hideTitle?: boolean
  subtitle?: React.ReactNode
  tooltipText?: string
  rightContent?: React.ReactNode
  rightCallback?: () => void
  isCheckbox?: boolean
  isCheckboxDisabled?: boolean
  onCheckedChange?: (checked: boolean | 'indeterminate') => void
  checked?: boolean
  required?: boolean
}

export function ModelConstraintCardContainer({
  children,
  className,
}: ModelConstraintCardContainerProps) {
  return (
    <div
      className={cn(
        'w-full px-4 py-3 border border-primary-border rounded-md flex-1',
        className,
      )}
    >
      {children}
    </div>
  )
}

export function ModelConstraintCardHeader({
  title,
  hideTitle = false,
  subtitle,
  tooltipText,
  rightContent,
  isCheckbox = false,
  isCheckboxDisabled = false,
  onCheckedChange,
  checked,
  required = false,
}: ModelConstraintCardHeaderProps) {
  const renderLeftContent = () => {
    if (isCheckbox) {
      return (
        <div className="flex items-center gap-1">
          <Checkbox
            label={title}
            checked={checked}
            onCheckedChange={onCheckedChange}
            disabled={isCheckboxDisabled}
          />

          {tooltipText && (
            <Tooltip simpleText={tooltipText}>
              <span>
                <Icon
                  iconName={IconName.InformationCircleIcon}
                  outline={true}
                  size="small"
                />
              </span>
            </Tooltip>
          )}
        </div>
      )
    }

    return (
      <div className="flex flex-col">
        <div className="flex items-center gap-1">
          {!hideTitle && (
            <div className="flex items-center gap-0.5">
              <Text
                variant={TextVariant.BodySubtext}
                fontWeight={FontWeight.Regular}
              >
                {title}
              </Text>
              {required && (
                <Text
                  variant={TextVariant.BodySubtext}
                  className="text-moment-red-500"
                >
                  *
                </Text>
              )}
            </div>
          )}
          {tooltipText && (
            <Tooltip simpleText={tooltipText}>
              <span>
                <Icon
                  iconName={IconName.InformationCircleIcon}
                  outline={true}
                  size="small"
                />
              </span>
            </Tooltip>
          )}
        </div>
        {subtitle && (
          <Text
            variant={TextVariant.BodySubtext}
            fontWeight={FontWeight.Regular}
            asChild
          >
            <span>{subtitle}</span>
          </Text>
        )}
      </div>
    )
  }

  return (
    <div className="flex justify-between items-center w-full">
      {renderLeftContent()}
      {rightContent}
    </div>
  )
}

export function ModelConstraintContent({
  children,
}: ModelConstraintContentProps) {
  return <div className="mt-2">{children}</div>
}
